import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, ExternalLink, Loader2, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import axios from 'axios';
import BrandLogo from '../components/BrandLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminFactCheckersPage = () => {
  const navigate = useNavigate();
  const [checkers, setCheckers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingChecker, setEditingChecker] = useState(null);
  const [error, setError] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    url: '',
    api_type: 'none',
    api_endpoint: '',
    api_key_env: '',
    search_url_template: '',
    category: 'general',
    description: '',
    priority: 1
  });

  useEffect(() => {
    checkAdminAccess();
    loadCheckers();
  }, []);

  const checkAdminAccess = () => {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (user && user.is_admin) {
      setIsAdmin(true);
    } else {
      setError('Admin access required');
    }
  };

  const loadCheckers = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/factcheckers/?active_only=false`);
      setCheckers(response.data);
    } catch (err) {
      setError('Failed to load fact-checkers');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      url: '',
      api_type: 'none',
      api_endpoint: '',
      api_key_env: '',
      search_url_template: '',
      category: 'general',
      description: '',
      priority: 1
    });
    setEditingChecker(null);
  };

  const handleAddChecker = async () => {
    if (!isAdmin) {
      setError('Admin access required');
      return;
    }

    setError('');
    try {
      const response = await axios.post(`${API}/factcheckers/`, formData, {
        withCredentials: true
      });
      setCheckers([...checkers, response.data]);
      setShowAddModal(false);
      resetForm();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Failed to add fact-checker');
      }
    }
  };

  const handleEditChecker = (checker) => {
    setEditingChecker(checker);
    setFormData({
      name: checker.name,
      url: checker.url,
      api_type: checker.api_type || 'none',
      api_endpoint: checker.api_endpoint || '',
      api_key_env: checker.api_key_env || '',
      search_url_template: checker.search_url_template || '',
      category: checker.category || 'general',
      description: checker.description || '',
      priority: checker.priority || 1
    });
    setShowAddModal(true);
  };

  const handleUpdateChecker = async () => {
    if (!isAdmin || !editingChecker) return;

    setError('');
    try {
      const response = await axios.put(
        `${API}/factcheckers/${editingChecker.checker_id}`,
        formData,
        { withCredentials: true }
      );
      setCheckers(checkers.map(c => c.checker_id === editingChecker.checker_id ? response.data : c));
      setShowAddModal(false);
      resetForm();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Failed to update fact-checker');
      }
    }
  };

  const handleDeleteChecker = async (checkerId) => {
    if (!isAdmin) return;
    if (!window.confirm('Are you sure you want to delete this fact-checker?')) return;

    try {
      await axios.delete(`${API}/factcheckers/${checkerId}`, {
        withCredentials: true
      });
      setCheckers(checkers.filter(c => c.checker_id !== checkerId));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete fact-checker');
    }
  };

  const handleSeedDefaults = async () => {
    if (!isAdmin) return;
    if (!window.confirm('This will add default verified fact-checkers. Continue?')) return;

    try {
      const response = await axios.post(`${API}/factcheckers/seed`, {}, {
        withCredentials: true
      });
      alert(`Added ${response.data.added} fact-checkers, skipped ${response.data.skipped} existing`);
      loadCheckers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to seed fact-checkers');
    }
  };

  const getApiTypeBadge = (apiType) => {
    switch (apiType) {
      case 'google_factcheck': return { label: 'Google API', className: 'bg-blue-600 text-white' };
      case 'custom_api': return { label: 'Custom API', className: 'bg-purple-600 text-white' };
      case 'scrape': return { label: 'Web Scrape', className: 'bg-orange-500 text-white' };
      default: return { label: 'Direct Link', className: 'bg-gray-500 text-white' };
    }
  };

  const getCategoryBadge = (category) => {
    switch (category) {
      case 'political': return 'bg-red-100 text-red-800';
      case 'health': return 'bg-green-100 text-green-800';
      case 'science': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isAdmin && !isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center">
        <Alert variant="destructive" className="max-w-md">
          <AlertDescription>
            Admin access required. Please contact an administrator.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo size="md" data-testid="brand-logo-header" />
            <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
              KaiCheckIt - Admin
            </span>
          </div>
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="flex items-center gap-2 hover:bg-blue-50 dark:hover:bg-slate-800 transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Header Section */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-800 dark:text-white mb-2">Verified Fact-Checkers</h1>
              <p className="text-slate-600 dark:text-slate-400">Manage Fact-Checker services for the Deep Dive feature</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleSeedDefaults}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Seed Defaults
              </Button>
              <Button
                onClick={() => {
                  resetForm();
                  setError('');
                  setShowAddModal(true);
                }}
                className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Fact-Checker
              </Button>
            </div>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">About Verified Fact-Checkers</h3>
            <p className="text-sm text-blue-700 dark:text-blue-400">
              These services power the "Explore Other FactCheckers" (Deep Dive) feature. 
              Services with API integration (like Google Fact Check) can be queried programmatically. 
              Others provide direct search links for users to verify claims manually.
            </p>
          </div>

          {/* Checkers Grid */}
          {isLoading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {checkers.sort((a, b) => a.priority - b.priority).map((checker) => (
                <Card key={checker.checker_id} className="hover:shadow-lg transition-shadow dark:bg-slate-800 dark:border-slate-700">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500 dark:text-slate-400">#{checker.priority}</span>
                        <CardTitle className="text-lg dark:text-white">{checker.name}</CardTitle>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditChecker(checker)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteChecker(checker.checker_id)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <a
                          href={checker.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                        >
                          {new URL(checker.url).hostname}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Badge className={getApiTypeBadge(checker.api_type).className}>
                          {getApiTypeBadge(checker.api_type).label}
                        </Badge>
                        <Badge className={getCategoryBadge(checker.category)}>
                          {checker.category}
                        </Badge>
                        {!checker.is_active && (
                          <Badge className="bg-red-100 text-red-800">Inactive</Badge>
                        )}
                      </div>
                      {checker.search_url_template && (
                        <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                          <Search className="w-3 h-3" />
                          <span>Search enabled</span>
                        </div>
                      )}
                      {checker.description && (
                        <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">{checker.description}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {checkers.length === 0 && !isLoading && (
            <div className="text-center py-20">
              <p className="text-slate-600 dark:text-slate-400 mb-4">No fact-checkers added yet</p>
              <Button onClick={handleSeedDefaults} className="bg-blue-600 hover:bg-blue-700">
                Seed Default Fact-Checkers
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Add/Edit Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => {
        setShowAddModal(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingChecker ? 'Edit Fact-Checker' : 'Add Verified Fact-Checker'}</DialogTitle>
            <DialogDescription>
              {editingChecker ? 'Update the fact-checker configuration' : 'Add a new verified fact-checking service for Deep Dive'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g., Snopes, PolitiFact"
              />
            </div>

            <div>
              <Label htmlFor="url">Base URL</Label>
              <Input
                id="url"
                type="url"
                value={formData.url}
                onChange={(e) => handleInputChange('url', e.target.value)}
                placeholder="https://www.snopes.com"
              />
            </div>

            <div>
              <Label htmlFor="api_type">Integration Type</Label>
              <Select value={formData.api_type} onValueChange={(value) => handleInputChange('api_type', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Direct Link (no API)</SelectItem>
                  <SelectItem value="google_factcheck">Google Fact Check API</SelectItem>
                  <SelectItem value="custom_api">Custom API</SelectItem>
                  <SelectItem value="scrape">Web Scrape</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.api_type !== 'none' && (
              <>
                <div>
                  <Label htmlFor="api_endpoint">API Endpoint</Label>
                  <Input
                    id="api_endpoint"
                    value={formData.api_endpoint}
                    onChange={(e) => handleInputChange('api_endpoint', e.target.value)}
                    placeholder="https://api.example.com/v1/search"
                  />
                </div>

                <div>
                  <Label htmlFor="api_key_env">API Key Environment Variable</Label>
                  <Input
                    id="api_key_env"
                    value={formData.api_key_env}
                    onChange={(e) => handleInputChange('api_key_env', e.target.value)}
                    placeholder="e.g., GOOGLE_FACTCHECK_API_KEY"
                  />
                  <p className="text-xs text-slate-500 mt-1">Name of the env variable storing the API key</p>
                </div>
              </>
            )}

            <div>
              <Label htmlFor="search_url_template">Search URL Template</Label>
              <Input
                id="search_url_template"
                value={formData.search_url_template}
                onChange={(e) => handleInputChange('search_url_template', e.target.value)}
                placeholder="https://www.snopes.com/?s={query}"
              />
              <p className="text-xs text-slate-500 mt-1">Use {'{query}'} as placeholder for search term</p>
            </div>

            <div>
              <Label htmlFor="category">Category</Label>
              <Select value={formData.category} onValueChange={(value) => handleInputChange('category', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="political">Political</SelectItem>
                  <SelectItem value="health">Health</SelectItem>
                  <SelectItem value="science">Science</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="priority">Display Priority</Label>
              <Input
                id="priority"
                type="number"
                min="1"
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', parseInt(e.target.value) || 1)}
              />
              <p className="text-xs text-slate-500 mt-1">Lower number = higher priority in list</p>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Brief description of this fact-checker"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={editingChecker ? handleUpdateChecker : handleAddChecker}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {editingChecker ? 'Update' : 'Add'} Fact-Checker
              </Button>
              <Button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFactCheckersPage;
